/**
 * creature/templates/horse.ts — The Noble Mount
 *
 * Horses are the quintessential mount: tall, fast, graceful. Their
 * skeleton is longer and taller than any other quadruped here, with
 * a pronounced mane and a gait system that goes walk -> trot -> canter -> gallop.
 * The rider sits at the shoulder joint, lifted by heightAtShoulder.
 */

import type { SkeletonDef } from "../skeleton.js";
import type { SpeciesTemplate, GaitPattern } from "../species.js";
import { registerSpecies, createBreed } from "../species.js";

// ─── Horse Skeleton ─────────────────────────────────────────────────────────

const HORSE_SKELETON: SkeletonDef = {
  id: "skeleton_horse",
  rootJointId: "hip",
  joints: [
    {
      id: "hip", parentId: null,
      restOffset: { x: 0, y: 0 }, restAngle: -0.3,
      minAngle: -1.0, maxAngle: 0.2, length: 20,
      tags: ["root", "hip"],
    },
    {
      id: "shoulder", parentId: "hip",
      restOffset: { x: 0, y: 0 }, restAngle: -0.1,
      minAngle: -0.3, maxAngle: 0.3, length: 10,
      tags: ["shoulder", "spine"],
    },
    {
      id: "neck", parentId: "shoulder",
      restOffset: { x: 0, y: 0 }, restAngle: -0.6,
      minAngle: -1.5, maxAngle: 0.3, length: 8,
      tags: ["neck"],
    },
    {
      id: "head", parentId: "neck",
      restOffset: { x: 0, y: 0 }, restAngle: 0.3,
      minAngle: -0.3, maxAngle: 0.8, length: 0,
      tags: ["head"],
    },
    // Tail — long and flowing
    {
      id: "tail_0", parentId: "hip",
      restOffset: { x: 6, y: -1 }, restAngle: -0.1,
      minAngle: -1.0, maxAngle: 1.5, length: 8,
      tags: ["tail"],
    },
    {
      id: "tail_1", parentId: "tail_0",
      restOffset: { x: 0, y: 0 }, restAngle: 0.1,
      minAngle: -1.0, maxAngle: 1.5, length: 8,
      tags: ["tail"],
    },
    {
      id: "tail_2", parentId: "tail_1",
      restOffset: { x: 0, y: 0 }, restAngle: 0.2,
      minAngle: -1.5, maxAngle: 2.0, length: 6,
      tags: ["tail", "tail_tip"],
    },
    // Ears — small, pointed, mobile
    {
      id: "ear_l", parentId: "head",
      restOffset: { x: -1.5, y: -2.5 }, restAngle: -1.5,
      minAngle: -2.0, maxAngle: -0.8, length: 3,
      tags: ["ear", "ear_l"],
    },
    {
      id: "ear_r", parentId: "head",
      restOffset: { x: 1.5, y: -2.8 }, restAngle: -1.5,
      minAngle: -2.0, maxAngle: -0.8, length: 3,
      tags: ["ear", "ear_r"],
    },
  ],
  drawOrder: [
    { name: "back_legs",   joints: ["hip"] },
    { name: "tail",        joints: ["hip", "tail_0", "tail_1", "tail_2"] },
    { name: "body",        joints: ["hip", "shoulder"] },
    { name: "front_legs",  joints: ["shoulder"] },
    { name: "mane",        joints: ["shoulder", "neck", "head"] },
    { name: "head",        joints: ["head", "neck", "ear_l", "ear_r"] },
    { name: "fire_glow",   joints: ["hip", "shoulder"] },
  ],
  ikChains: [
    {
      id: "look_at",
      jointIds: ["shoulder", "neck", "head"],
      targetMode: "point",
      iterations: 3,
      tolerance: 0.8,
    },
  ],
};

// ─── Horse Gaits ────────────────────────────────────────────────────────────

const HORSE_WALK: GaitPattern = {
  name: "walk",
  speedRange: [0, 2],
  legPhaseOffsets: {
    // 4-beat walk: each leg lands separately
    leg_front_l: 0,
    leg_back_r: Math.PI * 0.5,
    leg_front_r: Math.PI,
    leg_back_l: Math.PI * 1.5,
  },
  strideLength: 0.6,
  bodyBob: 0.2,
  bodyRoll: 0.08,
  headBob: 0.3,  // horses nod when walking
};

const HORSE_TROT: GaitPattern = {
  name: "trot",
  speedRange: [2, 4],
  legPhaseOffsets: {
    // 2-beat diagonal: front_l + back_r, then front_r + back_l
    leg_front_l: 0, leg_back_r: 0,
    leg_front_r: Math.PI, leg_back_l: Math.PI,
  },
  strideLength: 0.9,
  bodyBob: 0.5,   // bouncy
  bodyRoll: 0.12,
  headBob: 0.15,
};

const HORSE_CANTER: GaitPattern = {
  name: "canter",
  speedRange: [4, 7],
  legPhaseOffsets: {
    // 3-beat: back_r, then front_r + back_l (diagonal pair), then front_l (lead leg)
    leg_back_r: 0,
    leg_front_r: Math.PI * 0.4, leg_back_l: Math.PI * 0.4,
    leg_front_l: Math.PI * 0.8,
  },
  strideLength: 1.2,
  bodyBob: 0.65,
  bodyRoll: 0.06,
  headBob: 0.1,
};

const HORSE_GALLOP: GaitPattern = {
  name: "gallop",
  speedRange: [7, 14],
  legPhaseOffsets: {
    // 4-beat: each leg separate at high speed
    leg_back_r: 0,
    leg_back_l: 0.25,
    leg_front_r: Math.PI * 0.5,
    leg_front_l: Math.PI * 0.75,
  },
  strideLength: 2.0,
  bodyBob: 0.8,
  bodyRoll: 0.04,
  headBob: 0.05,
};

// ─── Horse Template ─────────────────────────────────────────────────────────

export const SPECIES_HORSE: SpeciesTemplate = {
  id: "horse_standard",
  displayName: "Horse",
  bodyPlan: "quadruped",
  skeleton: HORSE_SKELETON,
  size: "large",
  baseSpeed: 3.0,
  heightAtShoulder: 1.6,
  gaits: [HORSE_WALK, HORSE_TROT, HORSE_CANTER, HORSE_GALLOP],
  turnRate: Math.PI * 0.8,
  palette: {
    body: "#6a4a2a",
    markings: "#4a3420",
    eyes: "#402810",
    nose: "#3a2a1a",
    accent: "#2a1a0a",
    belly: "#7a5a3a",
  },
  bodyShape: {
    bodyLength: 20,
    bodyWidth: 5.5,
    headRadius: 5.0,
    tailSegments: 3,
    tailLength: 22,
    earStyle: "pointed",
    earSize: 0.8,
    legThickness: 2.2,
    hasMane: true,
    hasWings: false,
    neckLength: 2.0,
    snoutLength: 0.9,
    legLength: 1.8,
  },
  behaviorDefaults: {
    energy: 0.5,
    sociability: 0.5,
    curiosity: 0.4,
    obedience: 0.6,
    playfulness: 0.3,
    courage: 0.4,
    agility: 0.7,
    stamina: 0.85,
    nocturnality: 0.1,
    territoriality: 0.2,
    packInstinct: 0.6,
  },
  rideable: true,
  riderAttachJoint: "shoulder",
  riderOffsetZ: 1.2,
  saddleable: true,
  sounds: {
    idle: ["snort"],
    happy: ["nicker"],
    hungry: ["whinny"],
    walk: ["hoof_clop"],
    run: ["hoofbeat_rapid"],
    special: ["neigh", "whinny", "snort", "nicker"],
  },
  moodContribution: {
    warmth: 0.03,
    coziness: 0.04,
    energy: 0.05,
    nature: 0.08,
  },
};

// ─── Breed Variants ─────────────────────────────────────────────────────────

export const SPECIES_HORSE_BLACK = createBreed(SPECIES_HORSE, {
  id: "horse_black",
  displayName: "Black Stallion",
  palette: {
    ...SPECIES_HORSE.palette,
    body: "#1a1a1e",
    markings: "#101014",
    belly: "#252528",
    accent: "#0a0a0e",
  },
  behaviorDefaults: {
    ...SPECIES_HORSE.behaviorDefaults,
    courage: 0.7,
    obedience: 0.3,
    energy: 0.65,
  },
});

export const SPECIES_HORSE_WHITE = createBreed(SPECIES_HORSE, {
  id: "horse_white",
  displayName: "White Mare",
  palette: {
    ...SPECIES_HORSE.palette,
    body: "#e8e0d8",
    markings: "#d0c8c0",
    belly: "#f0ece8",
    accent: "#c8c0b8",
  },
  behaviorDefaults: {
    ...SPECIES_HORSE.behaviorDefaults,
    courage: 0.5,
    sociability: 0.6,
  },
});

export const SPECIES_HORSE_PAINT = createBreed(SPECIES_HORSE, {
  id: "horse_paint",
  displayName: "Paint Horse",
  palette: {
    ...SPECIES_HORSE.palette,
    body: "#c89060",
    markings: "#f0e8e0",
    belly: "#d8a878",
  },
  behaviorDefaults: {
    ...SPECIES_HORSE.behaviorDefaults,
    playfulness: 0.5,
    curiosity: 0.6,
  },
});

// ─── Register ───────────────────────────────────────────────────────────────

export function registerHorses(): void {
  registerSpecies(SPECIES_HORSE);
  registerSpecies(SPECIES_HORSE_BLACK);
  registerSpecies(SPECIES_HORSE_WHITE);
  registerSpecies(SPECIES_HORSE_PAINT);
}
